import { Injectable, inject } from '@angular/core';
import { Storage, getDownloadURL, ref, uploadBytes } from '@angular/fire/storage';
import imageCompression, { Options as ImageCompressionOptions } from 'browser-image-compression';

export interface FirebaseStorageUploadResult {
  url: string;
  storagePath: string;
}

@Injectable({
  providedIn: 'root',
})
export class FirebaseStorageService {
  private readonly storage = inject(Storage);
  private readonly compressibleMimeTypes = new Set([
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
  ]);

  async uploadUserProfilePhoto(file: File, uid: string): Promise<string> {
    return await this.uploadFile(
      file,
      `usersAttendance/${uid}/profile-${Date.now()}.${this.getFileExtension(file.name)}`,
      {
        maxSizeMB: 0.25,
        maxWidthOrHeight: 512,
        initialQuality: 0.82,
      }
    );
  }

  async uploadCenterAsset(
    file: File,
    centerKey: string,
    assetType: 'homeImage' | 'backgroundImage' | 'loadingImage' | 'logo'
  ): Promise<string> {
    return await this.uploadFile(
      file,
      `centers/${this.normalizePathSegment(centerKey)}/${assetType}`,
      this.getCenterAssetCompressionOptions(assetType)
    );
  }

  async uploadAttendanceJustificationAttachment(
    file: File,
    employeeId: string
  ): Promise<FirebaseStorageUploadResult> {
    const storagePath = `attendanceJustifications/${this.normalizePathSegment(employeeId)}/attachment-${Date.now()}.${this.getFileExtension(file.name)}`;
    const url = await this.uploadFile(file, storagePath, {
      maxSizeMB: 0.7,
      maxWidthOrHeight: 1600,
      initialQuality: 0.82,
    });

    return { url, storagePath };
  }

  private async uploadFile(
    file: File,
    objectPath: string,
    compressionOptions?: ImageCompressionOptions
  ): Promise<string> {
    const uploadableFile = await this.optimizeImageIfNeeded(file, compressionOptions);
    const objectRef = ref(this.storage, objectPath);

    await uploadBytes(objectRef, uploadableFile, {
      contentType: uploadableFile.type || file.type || 'application/octet-stream',
      cacheControl: 'public,max-age=3600',
    });

    return await getDownloadURL(objectRef);
  }

  private async optimizeImageIfNeeded(
    file: File,
    compressionOptions?: ImageCompressionOptions
  ): Promise<File> {
    if (!compressionOptions || !this.isCompressibleImage(file)) {
      return file;
    }

    try {
      return await imageCompression(file, {
        useWebWorker: true,
        ...compressionOptions,
      });
    } catch (error) {
      console.warn('Image optimization failed, uploading original file instead.', error);
      return file;
    }
  }

  private isCompressibleImage(file: File): boolean {
    return this.compressibleMimeTypes.has((file.type || '').toLowerCase());
  }

  private getCenterAssetCompressionOptions(
    assetType: 'homeImage' | 'backgroundImage' | 'loadingImage' | 'logo'
  ): ImageCompressionOptions {
    switch (assetType) {
      case 'logo':
        return {
          maxSizeMB: 0.3,
          maxWidthOrHeight: 512,
          initialQuality: 0.9,
        };
      case 'loadingImage':
        return {
          maxSizeMB: 0.35,
          maxWidthOrHeight: 800,
          initialQuality: 0.82,
        };
      case 'homeImage':
      case 'backgroundImage':
      default:
        return {
          maxSizeMB: 0.7,
          maxWidthOrHeight: 1600,
          initialQuality: 0.8,
        };
    }
  }

  private getFileExtension(fileName: string): string {
    const normalized = fileName.trim();
    const parts = normalized.split('.');
    return parts.length > 1 ? (parts.pop() || 'jpg').toLowerCase() : 'jpg';
  }

  private normalizePathSegment(value: string): string {
    return value.trim().replace(/[\\/#?%]+/g, '-');
  }
}
